import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../ui/Button';
import { useToast } from '../../contexts/ToastContext';
import {
  QR_STICKER_TEMPLATES,
  renderQrSticker,
  type QrStickerTemplate,
} from '../../utils/qrStickerCanvas';
import { loadImageSafe } from '../../utils/shareCardCanvas';
import apiService from '../../services/ApiService';

export interface QrPrintKitPanelProps {
  vehicleName: string;
  shareUrl: string;
  qrImageUrl: string;
  vehicleImageUrl?: string | null;
  vehicleId?: number;
  enabled?: boolean;
  className?: string;
}

const QrPrintKitPanel: React.FC<QrPrintKitPanelProps> = ({
  vehicleName,
  shareUrl,
  qrImageUrl,
  vehicleId,
  enabled = true,
  className = '',
}) => {
  const { showToast } = useToast();
  const template: QrStickerTemplate = 'square_2in';
  const spec = QR_STICKER_TEMPLATES[template];

  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const qrImgRef = useRef<HTMLImageElement | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const openedTracked = useRef(false);

  const shortUrl = shareUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const fileBase = `${vehicleName.replace(/\s+/g, '-').toLowerCase() || 'vehicle'}-2in-qr-kit`;

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setAssetsLoaded(false);
    loadImageSafe(qrImageUrl).then((qrImg) => {
      if (cancelled) return;
      qrImgRef.current = qrImg;
      setAssetsLoaded(Boolean(qrImg));
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, qrImageUrl]);

  useEffect(() => {
    if (enabled && !openedTracked.current) {
      openedTracked.current = true;
      void apiService.trackEvent('vehicle_qr_kit_opened', {
        vehicle_id: vehicleId,
        metadata: { template: 'square_2in' },
      });
    }
  }, [enabled, vehicleId]);

  const renderBlob = useCallback(async () => {
    if (!qrImgRef.current) throw new Error('QR not loaded');
    return renderQrSticker({
      template,
      vehicleName,
      shortUrl,
      qrImage: qrImgRef.current,
      accentColor: '#f97316',
    });
  }, [vehicleName, shortUrl]);

  const renderPreview = useCallback(async () => {
    if (!qrImgRef.current) return;
    setRendering(true);
    try {
      const blob = await renderBlob();
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch {
      showToast('Could not render print preview', 'error');
    } finally {
      setRendering(false);
    }
  }, [renderBlob, showToast]);

  useEffect(() => {
    if (enabled && assetsLoaded) void renderPreview();
  }, [enabled, assetsLoaded, renderPreview]);

  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    []
  );

  const handleDownloadPng = async () => {
    try {
      setRendering(true);
      const blob = await renderBlob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${fileBase}.png`;
      link.click();
      URL.revokeObjectURL(link.href);
      void apiService.trackEvent('vehicle_qr_png_downloaded', {
        vehicle_id: vehicleId,
        metadata: { width: spec.width, height: spec.height, dpi: 300 },
      });
      showToast('PNG print kit downloaded', 'success');
    } catch {
      showToast('PNG download failed', 'error');
    } finally {
      setRendering(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setRendering(true);
      const blob = await renderBlob();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('read failed'));
        reader.readAsDataURL(blob);
      });
      const { jsPDF } = await import('jspdf');
      // 2 inch square at exact physical size
      const doc = new jsPDF({ unit: 'in', format: [2, 2], orientation: 'portrait' });
      doc.addImage(dataUrl, 'PNG', 0, 0, 2, 2);
      doc.save(`${fileBase}.pdf`);
      void apiService.trackEvent('vehicle_qr_pdf_downloaded', {
        vehicle_id: vehicleId,
        metadata: { width_in: 2, height_in: 2, dpi: 300 },
      });
      showToast('PDF print kit downloaded', 'success');
    } catch {
      showToast('PDF download failed', 'error');
    } finally {
      setRendering(false);
    }
  };

  if (!enabled) return null;

  return (
    <section className={`card p-5 space-y-4 ${className}`} aria-labelledby="qr-print-kit-heading">
      <div>
        <h2 id="qr-print-kit-heading" className="heading-card text-sm">
          2 inch print kit
        </h2>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
          Download a square sticker sized for 2 inch labels at 300 DPI ({spec.width}×{spec.height}px). Print at 100%
          scale. Keep the white quiet zone around the code. Home printers, Sticker Mule, and local print shops all work
          when you choose 2×2 inch square stock.
        </p>
      </div>

      <div className="rounded-xl border border-white/[0.06] bg-[#0c0c0c] p-4 flex flex-col items-center">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={`Print preview for ${vehicleName}`}
            className="w-full max-w-[280px] rounded-lg"
          />
        ) : (
          <p className="text-sm text-gray-500 py-8">{rendering ? 'Rendering preview…' : 'Loading QR…'}</p>
        )}
        <p className="text-xs text-gray-600 mt-3">{spec.physicalSize} · {spec.useCase}</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button variant="primary" size="sm" loading={rendering} onClick={() => void handleDownloadPng()} disabled={!assetsLoaded}>
          Download PNG
        </Button>
        <Button variant="secondary" size="sm" loading={rendering} onClick={() => void handleDownloadPdf()} disabled={!assetsLoaded}>
          Download PDF
        </Button>
        <Button variant="ghost" size="sm" onClick={() => void renderPreview()} disabled={!assetsLoaded}>
          Refresh preview
        </Button>
      </div>

      <ol className="text-xs text-gray-500 space-y-1 list-decimal list-inside leading-relaxed">
        <li>Download PNG or PDF at full resolution.</li>
        <li>Print at 100% scale. Do not fit to page.</li>
        <li>Trim along the outer edge or use 2 inch square label stock.</li>
        <li>Scan the printed sticker once before the show to confirm the public build link.</li>
      </ol>
    </section>
  );
};

export default QrPrintKitPanel;
